import type { ReactNode } from "react";
import styles from "./IconCard.module.css";

interface Props {
  title: ReactNode;
  /** An icon in the red badge, or `step` for a numbered badge ("How it works"). */
  icon?: ReactNode;
  step?: number;
  children?: ReactNode;
  /** Heading level; h2 by default, h3 inside a section that has its own h2. */
  as?: "h2" | "h3";
}

/**
 * A white card with a red circular badge beside its heading, and its content lined up under the
 * heading text (the account and contractor pages).
 */
export function IconCard({ title, icon, step, children, as: Heading = "h2" }: Props) {
  return (
    <section className={styles.card}>
      <div className={styles.head}>
        <span className={styles.badge} aria-hidden="true">
          {step ?? icon}
        </span>
        <Heading className={styles.title}>{title}</Heading>
      </div>
      {children && <div className={styles.body}>{children}</div>}
    </section>
  );
}

/** A large page title with the short red underline, and an optional line under it. */
export function PageTitle({
  children,
  subtitle,
  actions,
}: {
  children: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={styles.pageTitle}>
      <div>
        <h1>{children}</h1>
        {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}

/** A load failure: the warning notice and a retry button (not a card, so it reads as an error). */
export function LoadError({ children, onRetry }: { children: ReactNode; onRetry: () => void }) {
  return (
    <div>
      <div className="notice notice--warn" role="alert">
        {children}
      </div>
      <button type="button" className="btn" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}
